import { useAsync } from 'react-use';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ErrorDisplay, Loading } from './utils';

export interface StudentProfile {
  phystech_email: string
  mipt_tech_tag?: string | null
  last_name?: string
  first_name?: string
  middle_name?: string | null
  vk_id?: string | null
  gmail?: string | null
}

export interface Profile {
  sub: string
  display_name: string
  preferred_username: string
  groups: string[]
  roles: string[]
  exp: number
  student_profile?: StudentProfile | null
}

export interface Identity {
  token: string
  profile: Profile
}

export interface AuthContext {
  identity(): Promise<Identity>
  /** Dev-only role override (see backend allow_role_override): roles listed
   *  here are stripped server-side on every request, to test as a
   *  lower-privileged user without a second account. */
  getDisabledRoles(): string[]
  setDisabledRoles(roles: string[]): void
}

const DISABLED_ROLES_STORAGE_KEY = 'lectory_debug_disabled_roles';

export const ADMIN_ROLE = 'admin';
export const REVIEWER_ROLE = 'reviewer';

const REVIEW_ROLES = ['admin', 'reviewer'];

// Supervisor is its own role (granted in the IdP via the supervisors group),
// gating the most sensitive admin areas (semesters, AWH). Not implied by admin.
export const SUPERVISOR_ROLE = 'supervisor';

export const authContext = createContext<AuthContext | null>(null);

class AuthContextImpl implements AuthContext {
  private currentIdentity?: Identity
  private refreshPromise: Promise<Identity>
  private disabledRoles: string[]

  constructor() {
    this.disabledRoles = AuthContextImpl.loadDisabledRoles();
    this.refreshPromise = this.refresh();
  }

  private static loadDisabledRoles(): string[] {
    try {
      const raw = localStorage.getItem(DISABLED_ROLES_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  getDisabledRoles(): string[] {
    return this.disabledRoles;
  }

  setDisabledRoles(roles: string[]): void {
    this.disabledRoles = roles;
    localStorage.setItem(DISABLED_ROLES_STORAGE_KEY, JSON.stringify(roles));
  }

  private debugHeaders(): Record<string, string> {
    return this.disabledRoles.length > 0
      ? { 'x-debug-disabled-roles': this.disabledRoles.join(',') }
      : {};
  }

  async identity(): Promise<Identity> {
    const expiresInMs = (this.currentIdentity ? this.currentIdentity.profile.exp * 1_000 - Date.now() : 0);
    if (this.currentIdentity && expiresInMs >= 60_000) {
      return this.currentIdentity;
    } else {
      return this.refreshPromise;
    }
  }

  private async refresh(): Promise<Identity> {
    const rsp = await fetch('/api/login/token', { method: 'POST', headers: this.debugHeaders() });
    if (!rsp.ok) {
      const location = rsp.headers.get('location');
      if (rsp.status == 401 && location) {
        window.location.replace(location);
        return new Promise(() => {}); // navigating away — never resolve
      } else {
        throw new Error(`Authentication failed: ${rsp.status} ${rsp.statusText}: ${await rsp.text()}`);
      }
    }
    const { id_token, profile } = await rsp.json();
    const expiresInMs = profile.exp * 1_000 - Date.now();
    // Ограничение максимум 12 часов (43_200_000 мс) предотвращает переполнение 32-битного int в setTimeout
    const timeoutMs = Math.max(10_000, Math.min(0.8 * expiresInMs, 12 * 3600 * 1000));
    setTimeout(() => { this.refreshPromise = this.refresh() }, timeoutMs);
    const identity: Identity = {
      token: id_token,
      profile: {
        sub: profile.sub,
        display_name: profile.name,
        preferred_username: profile.preferred_username,
        groups: profile.groups,
        roles: profile.roles,
        exp: profile.exp,
        student_profile: profile.student_profile ?? null,
      },
    };
    this.currentIdentity = identity;
    return identity;
  }
}

export const WithAuthProvider = (props: {
  children: JSX.Element,
}) => {
  const authContextImpl = useMemo(() => new AuthContextImpl(), []);
  const { error, loading } = useAsync(useCallback(() => authContextImpl.identity(), [authContextImpl]));
  if (error) {
    return <ErrorDisplay error={error} />
  }
  if (loading) {
    return <Loading />
  }
  return <authContext.Provider value={authContextImpl}>{props.children}</authContext.Provider>
}

export const useAuth = () => {
  const auth = useContext(authContext);
  if (!auth) {
    throw new Error('Missing authContext');
  }
  return auth;
}

export const isAdmin = () => {
  const auth = useAuth();
  return useAsync(async () => {
    const identity = await auth.identity();
    return identity.profile.roles.includes(ADMIN_ROLE);
  }, [auth]);
}

export const useReviewAccess = () => {
  const auth = useAuth();
  return useAsync(async () => {
    const identity = await auth.identity();
    return identity.profile.roles.some(role => REVIEW_ROLES.includes(role));
  }, [auth]);
}

export const useSupervisorAccess = () => {
  const auth = useAuth();
  return useAsync(async () => {
    const identity = await auth.identity();
    return identity.profile.roles.includes(SUPERVISOR_ROLE);
  }, [auth]);
}
